import { cn } from 'helpers/cn';
import { NavLink } from '@/components/NavLink';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useOrganizations } from '@/state/useOrganizations';
import { useBillingAllowance } from '@/state/billing/useBillingAllowance';
import { useBillingState } from '@/state/billing/useBillingState';
import { usageMeter } from '@/state/billing/usage-meter';

type UsageMeterProps = { isMobile: boolean };

/**
 * Sessions used against the plan, for the account in scope: the client an agency has
 * selected, the caller's own account otherwise. Reads the same queries as Settings,
 * Billing, so the two cannot disagree. It links there, and is hidden when there is
 * nothing to measure. The AI credits have their own meter beside it (AiCreditMeter). On a
 * phone it drops to the session count alone.
 */
export const UsageMeter = ({ isMobile }: UsageMeterProps) => {
  const { organization } = useOrganizations();
  const accountId = organization?.id ?? null;
  const { allowance } = useBillingAllowance({ accountId });
  const { billingState } = useBillingState({ accountId });
  const meter = usageMeter({ allowance, billingActive: billingState?.billingActive });
  if (!meter) return null;

  if (meter.noPlan) {
    return (
      <MeterTooltip lines={meter.details}>
        <NavLink
          to="/settings/billing"
          aria-label="No active plan. Nothing serves to this account's websites. Open billing."
          className="bg-danger-solid text-danger-solid-fg focus-visible:ring-ring shrink-0 rounded-pill px-2.5 py-1 font-mono text-micro font-medium focus-visible:ring-2 focus-visible:outline-none"
        >
          {isMobile ? 'No plan' : 'No active plan'}
        </NavLink>
      </MeterTooltip>
    );
  }

  const { sessions } = meter;
  if (!sessions) return null;

  return (
    <MeterTooltip lines={meter.details}>
      <NavLink
        to="/settings/billing"
        aria-label={`Usage: ${sessions.text} sessions this period`}
        // The platform tour's usage step; the No active plan pill above is not usage, so it has none.
        data-tour="header-usage"
        className={cn(
          'focus-visible:ring-ring flex shrink-0 items-center gap-2 rounded-pill border px-2.5 py-1 font-mono text-micro transition-colors duration-150 focus-visible:ring-2 focus-visible:outline-none',
          sessions.over
            ? 'border-danger-line bg-danger-bg text-danger'
            : sessions.warn
              ? 'border-warn-line bg-warn-bg text-warn'
              : 'border-line bg-field text-dim hover:text-foreground',
        )}
      >
        <span className="flex items-center gap-1.5">
          {sessions.ratio !== null && (
            <span className="bg-line hidden h-1 w-8 overflow-hidden rounded-pill sm:block" aria-hidden>
              <span
                className={cn(
                  'block h-full',
                  sessions.over ? 'bg-danger' : sessions.warn ? 'bg-warn' : 'bg-foreground',
                )}
                style={{ width: `${Math.round(sessions.ratio * 100)}%` }}
              />
            </span>
          )}
          <span>{isMobile ? sessions.text.split(' / ')[0] : sessions.text}</span>
        </span>
      </NavLink>
    </MeterTooltip>
  );
};

type MeterTooltipProps = { lines: string[]; children: React.ReactNode };

/** The hover that explains the pill: the figures in words, when they reset, and what its colour means. */
const MeterTooltip = ({ lines, children }: MeterTooltipProps) => (
  <TooltipProvider delayDuration={150}>
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side="bottom" align="end" className="max-w-xs space-y-1 text-body">
        {lines.map((line, index) => (
          <p key={line} className={index === 0 ? 'm-0 font-medium' : 'supporting m-0'}>
            {line}
          </p>
        ))}
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);
